/**
 * Activations API. Mirrors the /api/activations and /api/activation-master-data
 * routers; re-exported from services/api.js like the other domain modules.
 */

import api from '../apiClient';

export const activationAPI = {
  // Create flow
  getCreateOptions: () => api.get('/activations/create-options'),
  getSelectableExpenses: (params) => api.get('/activations/selectable-expenses', { params }),
  searchProducts: (params) => api.get('/activations/product-search', { params }),
  preview: (payload) => api.post('/activations/preview', payload),
  create: (payload) => api.post('/activations', payload),

  // Browse
  getAll: (params) => api.get('/activations', { params }),
  getById: (id) => api.get(`/activations/${id}`),
  getActivity: (id) => api.get(`/activations/${id}/activity`),
  getDashboard: () => api.get('/activations/dashboard'),
  getMyTasks: () => api.get('/activations/my-tasks'),

  // Grid
  getGrid: (params) => api.get('/activations/grid', { params }),
  exportGrid: () => api.get('/activations/grid/export', { responseType: 'blob' }),
  updateGridCell: (payload) => api.post('/activations/grid/cell', payload),

  // Edit
  update: (id, payload) => api.patch(`/activations/${id}`, payload),
  archive: (id) => api.patch(`/activations/${id}/archive`),
  unarchive: (id) => api.patch(`/activations/${id}/unarchive`),
  remove: (id) => api.delete(`/activations/${id}`),
  unlinkExpense: (id, expenseId) => api.delete(`/activations/${id}/expenses/${expenseId}`),

  // Steps
  submitStep: (id, order) => api.post(`/activations/${id}/steps/${order}/submit`),
  completeStep: (id, order) => api.post(`/activations/${id}/steps/${order}/complete`),
  approveStep: (id, order) => api.post(`/activations/${id}/steps/${order}/approve`),
  rejectStep: (id, order, note) => api.post(`/activations/${id}/steps/${order}/reject`, { note }),
  overrideDeadline: (id, order, deadline) => api.patch(`/activations/${id}/steps/${order}/deadline`, { deadline }),
  setAssignee: (id, order, assignee) => api.patch(`/activations/${id}/steps/${order}/assignee`, { assignee }),
  stepCandidates: (id, order) => api.get(`/activations/${id}/steps/${order}/candidates`),

  // Evidence
  getComments: (id, params) => api.get(`/activations/${id}/comments`, { params }),
  addComment: (id, order, body) => api.post(`/activations/${id}/steps/${order}/comments`, { body }),
  getMentionableUsers: () => api.get('/activations/mentionable-users'),
  getAssets: (id) => api.get(`/activations/${id}/assets`),
  attachLink: (id, order, payload) => api.post(`/activations/${id}/steps/${order}/links`, payload),
  // The file goes through the API, not straight to the bucket: a presigned PUT
  // from the page is cross-origin and needs a CORS rule on the bucket.
  assetUrl: (id, assetId, disposition) => api.get(`/activations/${id}/assets/${assetId}/download`,
    { params: disposition ? { disposition } : undefined }),
  uploadFile: (id, order, file, onProgress) => {
    const body = new FormData();
    body.append('file', file);
    return api.post(`/activations/${id}/steps/${order}/upload`, body, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (e) => onProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0)
        : undefined
    });
  },
  deleteAsset: (id, assetId) => api.delete(`/activations/${id}/assets/${assetId}`)
};

export const activationProcessAPI = {
  getAll: (params) => api.get('/activation-master-data/templates', { params }),
  getMatchableCategories: () => api.get('/activation-master-data/templates/matchable-categories'),
  create: (payload) => api.post('/activation-master-data/templates', payload),
  update: (id, payload) => api.put(`/activation-master-data/templates/${id}`, payload),
  duplicate: (id) => api.post(`/activation-master-data/templates/${id}/duplicate`),
  remove: (id) => api.delete(`/activation-master-data/templates/${id}`)
};

export const blockingDateAPI = {
  getAll: (params) => api.get('/activation-master-data/blocking-dates', { params }),
  create: (payload) => api.post('/activation-master-data/blocking-dates', payload),
  update: (id, payload) => api.put(`/activation-master-data/blocking-dates/${id}`, payload),
  remove: (id) => api.delete(`/activation-master-data/blocking-dates/${id}`)
};

export const activationVenueAPI = {
  getAll: (params) => api.get('/activation-master-data/venues', { params }),
  create: (payload) => api.post('/activation-master-data/venues', payload),
  update: (id, payload) => api.put(`/activation-master-data/venues/${id}`, payload),
  remove: (id) => api.delete(`/activation-master-data/venues/${id}`)
};
